import type { PaginationMeta, SortDirection } from "./usePagination";
import usePersistedPagination from "./usePersistedPagination";

import { TWENTY_FIVE } from "@/app/constants/constants";

type UseLocalPaginationReturnProps = {
  localPagination: PaginationMeta;
  setLocalPagination: React.Dispatch<React.SetStateAction<PaginationMeta>>;
  paginationQuery: string;
  resetToFirstPage: () => void;
  isLoading: boolean;
};

/**
 * Thin wrapper around usePersistedPagination for feature data hooks that do
 * not need the full useTableQueryState filter pipeline. Passes through the
 * optional scope inputs so tab- and table-scoped pages persist pagination
 * independently per (pathname, activeTab, tableId) key.
 *
 * @param {number} limit - Default page size.
 * @param {string} activeTab - Active tab id for tab-scoped tables.
 * @param {string} tableId - Table id for pages rendering multiple tables.
 * @param {object} [defaultSort] - Tab-specific default sort used when no persisted sort exists.
 * @param {string} [defaultSort.sortBy] - Default sort field.
 * @param {string} [defaultSort.orderBy] - Default sort direction.
 * @returns {UseLocalPaginationReturnProps} Persisted pagination state and helpers.
 */
const useLocalPaginationHelper = (
  limit: number = TWENTY_FIVE,
  activeTab?: string,
  tableId?: string,
  defaultSort?: { sortBy?: string; orderBy?: SortDirection },
): UseLocalPaginationReturnProps => {
  const {
    localPagination,
    paginationQuery,
    resetToFirstPage,
    setLocalPagination,
    isLoading,
  } = usePersistedPagination(limit, activeTab, tableId, { defaultSort });

  return {
    localPagination,
    setLocalPagination,
    paginationQuery,
    resetToFirstPage,
    isLoading,
  };
};

export default useLocalPaginationHelper;
